import { hasActiveModuleGrant } from '@realadvisor/constants';

import { type Me } from '@modules/_shared/providers/AppDataProvider';
import { MODULES_AI_CHAT, MODULES_BROKERAGE } from '@shared/constants/module';

/**
 * True when a user has explicitly been assigned neither CRM access role.
 * Accepts both JWT-claim and GraphQL-user values so callers retain their own
 * data boundary while sharing the access decision.
 */
export const isNeitherAdminNorBroker = (
  isAdmin: boolean | null | undefined,
  isBroker: boolean | null | undefined,
): boolean => isAdmin === false && isBroker === false;

/**
 * True for RealAdvisor staff: an admin who also owns a @realadvisor.com email
 * address. Used to grant implicit access to features still rolling out — see
 * `canAccessChat`. The Agent Academy used to be gated on this and no longer is;
 * it is available to every CRM user.
 */
export const isRealAdvisorAdmin = (me: Me): boolean => {
  const isAdmin = me?.is_admin === true;
  const hasRealAdvisorEmail =
    me?.emails?.some(({ email }) =>
      email?.toLowerCase().endsWith('@realadvisor.com'),
    ) === true;

  return isAdmin && hasRealAdvisorEmail;
};

/**
 * Gates the AI Chat: enabled per user via the "ai_chat" CRM module
 * (pilot rollout), with implicit access for RealAdvisor admins.
 */
export const canAccessChat = (me: Me): boolean => {
  return (
    hasActiveModuleGrant(me?.module_grants, MODULES_AI_CHAT) ||
    isRealAdvisorAdmin(me)
  );
};

/**
 * The MLS is available only to Swiss users with the brokerage module.
 */
export const canAccessMls = (me: Me, countryCode: string): boolean =>
  countryCode === 'CH' &&
  hasActiveModuleGrant(me?.module_grants, MODULES_BROKERAGE);
